/**
 * Client-side pattern formatter for live preview.
 * Mirrors the server-side PatternFormatter logic.
 *
 * Supported tokens:
 *   {YYYY}     - 4-digit year
 *   {YY}       - 2-digit year
 *   {MM}       - 2-digit month
 *   {N}        - plain counter (no padding)
 *   {####}     - counter padded to number-of-hashes digits
 *   {HEX8}     - mock hex token (shows placeholder for preview)
 *   {INITIALS} - mock initials (shows "JK" for preview)
 */
export function previewPattern(pattern: string, counter: number, now = new Date()): string {
  const yyyy = String(now.getFullYear())
  const yy = String(now.getFullYear() % 100).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')

  let result = pattern
    .replace('{YYYY}', yyyy)
    .replace('{YY}', yy)
    .replace('{MM}', mm)
    .replace('{INITIALS}', 'JK')  // mock initials for preview
    .replace(/\{(#+)\}/g, (_, hashes: string) =>
      String(counter).padStart(hashes.length, '0')
    )
    .replace('{N}', String(counter))
    .replace('{HEX8}', '12AB34CD')  // mock for preview

  return result
}

/**
 * Returns true if the pattern contains at least one counter token.
 */
export function hasCounterToken(pattern: string): boolean {
  return /\{N\}|\{#+\}|\{HEX8\}/.test(pattern)
}

/**
 * Hard ceiling for a generated number length (mirrors server PatternFormatter.MAX_LENGTH).
 */
export const MAX_NUMBER_LENGTH = 20

// Worst-case widths per token - must match backend PatternFormatter.tokenWidth().
const TOKEN_WIDTHS: Record<string, number> = {
  YYYY: 4,
  YY: 2,
  MM: 2,
  N: 5,
  HEX8: 8,
  INITIALS: 3,
}

/**
 * Estimates the worst-case length of a number generated from the given pattern.
 * Mirrors the server-side PatternFormatter.estimateMaxLength() so the admin UI
 * can show a live counter and block save before the request hits the API.
 */
export function estimateMaxLength(pattern: string): number {
  if (!pattern) return 0
  const tokenRegex = /\{(YYYY|YY|MM|N|HEX8|INITIALS|#+)\}/g
  let length = 0
  let lastEnd = 0
  let match: RegExpExecArray | null
  while ((match = tokenRegex.exec(pattern)) !== null) {
    length += match.index - lastEnd
    const token = match[1]
    length += token.startsWith('#') ? token.length : (TOKEN_WIDTHS[token] ?? token.length)
    lastEnd = tokenRegex.lastIndex
  }
  length += pattern.length - lastEnd
  return length
}
